
import React, { useMemo, useState } from 'react';
import { Language, Order, KolProfile, ProjectProfile } from '../types';
import { TRANSLATIONS } from '../translations';
import { StatCard } from './StatCard';
import { DollarSign, Users, AlertCircle, HardDrive, ArrowUpRight, Share2, Coins, TrendingUp, Search } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from 'recharts';
import { MOCK_KOLS } from '../constants';

interface AdminDashboardProps {
  language: Language;
  orders: Order[];
  usersCount: number; // Projects + KOLs
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ language, orders, usersCount }) => {
  const t = TRANSLATIONS[language];
  const [activeTab, setActiveTab] = useState<'overview' | 'tokens' | 'kols'>('overview');
  const [kolSearch, setKolSearch] = useState('');

  // --- Calculate Metrics ---
  const totalGmv = useMemo(() => orders.reduce((acc, order) => acc + order.amount, 0), [orders]);
  // Platform Commission is 20%
  const totalRevenue = useMemo(() => totalGmv * 0.20, [totalGmv]);
  const activeDisputes = 2; // Mock data

  // --- Mock Chart Data for Revenue Growth ---
  const revenueData = [
    { name: 'Jan', value: 1200 },
    { name: 'Feb', value: 1900 },
    { name: 'Mar', value: 3400 },
    { name: 'Apr', value: 2800 },
    { name: 'May', value: 5200 },
    { name: 'Jun', value: 7500 },
  ];

  // --- Mock Token Rounds Data ---
  const TOKEN_ROUNDS = [
      { id: 1, fdv: 10_000_000, price: 0.010, totalTokens: 20_000_000, raisedTokens: 20_000_000, status: 'Completed' },
      { id: 2, fdv: 12_000_000, price: 0.012, totalTokens: 20_000_000, raisedTokens: 18_500_000, status: 'Active' },
      { id: 3, fdv: 15_000_000, price: 0.015, totalTokens: 20_000_000, raisedTokens: 0, status: 'Upcoming' },
      { id: 4, fdv: 18_000_000, price: 0.018, totalTokens: 20_000_000, raisedTokens: 0, status: 'Upcoming' },
      { id: 5, fdv: 20_000_000, price: 0.020, totalTokens: 20_000_000, raisedTokens: 0, status: 'Upcoming' },
  ];

  // --- Enhanced KOL Intelligence Data ---
  // Merging MOCK_KOLS with hypothetical referral/token data
  const KOL_INTELLIGENCE = MOCK_KOLS.map(kol => {
      // Fake some data for demo
      const inviteCount = Math.floor(Math.random() * 50);
      const invitedBy = Math.random() > 0.7 ? '@CryptoGod' : (Math.random() > 0.5 ? '@AlphaHunter' : '-');
      const tokensHeld = Math.floor(Math.random() * 50000);
      const totalEarnings = orders.filter(o => o.kolId === kol.id && o.status === 'Completed').reduce((acc, o) => acc + o.amount, 0) + (inviteCount * 50); // Base + referral bonus

      return {
          ...kol,
          inviteCount,
          invitedBy,
          tokensHeld,
          totalEarnings
      };
  });

  const filteredKols = KOL_INTELLIGENCE.filter(k => 
      k.name.toLowerCase().includes(kolSearch.toLowerCase()) || 
      k.handle.toLowerCase().includes(kolSearch.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <HardDrive className="text-amber-500" />
            {t.adminOverview}
          </h1>
          <p className="text-slate-400 text-sm">{t.welcomeAdmin}</p>
        </div>
        <div className="flex gap-2">
            <span className="bg-amber-500/10 text-amber-500 px-3 py-1 rounded-full text-xs font-bold border border-amber-500/20 flex items-center gap-1">
                {t.commissionRate}: 20%
            </span>
        </div>
      </div>

      {/* Admin Tabs */}
      <div className="flex gap-4 border-b border-slate-700">
          <button 
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-2 text-sm font-bold transition-colors border-b-2 ${activeTab === 'overview' ? 'border-indigo-500 text-white' : 'border-transparent text-slate-400 hover:text-white'}`}
          >
              {t.overview}
          </button>
          <button 
            onClick={() => setActiveTab('tokens')}
            className={`pb-3 px-2 text-sm font-bold transition-colors border-b-2 ${activeTab === 'tokens' ? 'border-indigo-500 text-white' : 'border-transparent text-slate-400 hover:text-white'}`}
          >
              {t.tokenSalesAnalytics}
          </button>
          <button 
            onClick={() => setActiveTab('kols')}
            className={`pb-3 px-2 text-sm font-bold transition-colors border-b-2 ${activeTab === 'kols' ? 'border-indigo-500 text-white' : 'border-transparent text-slate-400 hover:text-white'}`}
          >
              {t.kolIntelligence}
          </button>
      </div>

      {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-in fade-in slide-in-from-bottom-2">
                <StatCard 
                label={t.totalRevenue} 
                value={`$${totalRevenue.toLocaleString()}`} 
                trend="+18%" 
                trendUp={true} 
                icon={DollarSign} 
                colorClass="text-amber-500" 
                comparisonText={t.vsLastMonth}
                />
                <StatCard 
                label={t.totalGmv} 
                value={`$${totalGmv.toLocaleString()}`} 
                trend="+22%" 
                trendUp={true} 
                icon={DollarSign} 
                colorClass="text-blue-500" 
                comparisonText={t.vsLastMonth}
                />
                <StatCard 
                label={t.totalUsers} 
                value={usersCount.toString()} 
                trend="+45" 
                trendUp={true} 
                icon={Users} 
                colorClass="text-indigo-500" 
                comparisonText="new users"
                />
                <StatCard 
                label={t.activeDisputes} 
                value={activeDisputes.toString()} 
                trend="Action Req." 
                trendUp={false} 
                icon={AlertCircle} 
                colorClass="text-rose-500" 
                comparisonText="2 pending"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Revenue Chart */}
                <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Platform Revenue Growth</h3>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                        <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis dataKey="name" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }} />
                        <Area type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                    </AreaChart>
                    </ResponsiveContainer>
                </div>
                </div>

                {/* Recent Transactions List */}
                <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-slate-700">
                        <h3 className="text-lg font-bold text-white">{t.recentTransactions}</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto max-h-[300px] p-2">
                        {orders.map((order) => (
                            <div key={order.id} className="p-3 mb-2 rounded-lg bg-slate-900/50 hover:bg-slate-900 border border-slate-700/50 flex justify-between items-center transition-colors">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono mb-1">{order.id.slice(-8)}</p>
                                    <p className="text-sm font-medium text-white">{order.kolName}</p>
                                    <p className="text-xs text-slate-400">{order.projectName}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-bold text-white">${order.amount}</p>
                                    <p className="text-xs text-amber-500 font-medium">
                                        +${(order.amount * 0.20).toFixed(2)} Fee
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="p-4 border-t border-slate-700 bg-slate-900/30">
                        <button className="w-full py-2 text-sm text-indigo-400 hover:text-white flex items-center justify-center gap-1">
                            View Financial Report <ArrowUpRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
          </>
      )}

      {activeTab === 'tokens' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                      <h3 className="font-bold text-white mb-6 flex items-center gap-2">
                          <Coins className="text-indigo-400" /> {t.tokenSalesAnalytics}
                      </h3>
                      
                      {/* Overall Progress */}
                      <div className="mb-8 p-4 bg-slate-900/50 rounded-xl border border-slate-700">
                          <div className="flex justify-between items-end mb-2">
                              <div>
                                  <p className="text-xs text-slate-400 uppercase">{t.totalRaisedUsdt}</p>
                                  <p className="text-3xl font-bold text-white">$385,000</p>
                              </div>
                              <div className="text-right">
                                  <p className="text-xs text-slate-400 uppercase">{t.totalTokensSold}</p>
                                  <p className="text-xl font-bold text-emerald-400">38.5M / 100M</p>
                              </div>
                          </div>
                          <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 w-[38.5%]"></div>
                          </div>
                      </div>

                      <div className="space-y-4">
                          {TOKEN_ROUNDS.map(round => {
                              const percentage = (round.raisedTokens / round.totalTokens) * 100;
                              return (
                                  <div key={round.id} className="p-3 bg-slate-900/30 rounded-lg border border-slate-700/50">
                                      <div className="flex justify-between items-center mb-2">
                                          <div className="flex items-center gap-2">
                                              <span className={`w-2 h-2 rounded-full ${round.status === 'Completed' ? 'bg-emerald-500' : round.status === 'Active' ? 'bg-indigo-500 animate-pulse' : 'bg-slate-500'}`}></span>
                                              <span className="font-bold text-sm text-white">{t.round} {round.id}</span>
                                              <span className="text-xs text-slate-500">Price: ${round.price}</span>
                                          </div>
                                          <span className="text-xs font-mono text-white">{(round.raisedTokens / 1000000).toFixed(1)}M / {(round.totalTokens / 1000000).toFixed(0)}M</span>
                                      </div>
                                      <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                          <div 
                                            className={`h-full ${round.status === 'Completed' ? 'bg-emerald-500' : 'bg-indigo-500'}`} 
                                            style={{ width: `${percentage}%` }}
                                          ></div>
                                      </div>
                                  </div>
                              );
                          })}
                      </div>
                  </div>

                  <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 flex flex-col justify-center items-center text-center">
                      <div className="w-20 h-20 bg-indigo-500/20 rounded-full flex items-center justify-center mb-4">
                          <TrendingUp size={40} className="text-indigo-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Fundraising Velocity</h3>
                      <p className="text-slate-400 mb-6 max-w-xs">Analysis shows Round 2 is filling 15% faster than Round 1 due to high KOL referral activity.</p>
                      <button className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors">
                          Download Detailed Report
                      </button>
                  </div>
              </div>
          </div>
      )}

      {activeTab === 'kols' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="flex justify-between items-center bg-slate-800 p-4 rounded-xl border border-slate-700">
                  <h3 className="font-bold text-white flex items-center gap-2">
                      <Users className="text-indigo-400" /> {t.kolIntelligence}
                  </h3>
                  <div className="relative">
                      <Search className="absolute left-3 top-2.5 text-slate-500 w-4 h-4" />
                      <input 
                        type="text" 
                        placeholder="Search KOL..." 
                        value={kolSearch}
                        onChange={(e) => setKolSearch(e.target.value)}
                        className="bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 w-64"
                      />
                  </div>
              </div>

              <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                      <table className="w-full text-left">
                          <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
                              <tr>
                                  <th className="px-6 py-4">KOL</th>
                                  <th className="px-6 py-4">Trust Score</th>
                                  <th className="px-6 py-4">{t.earnings}</th>
                                  <th className="px-6 py-4">{t.inviteCount}</th>
                                  <th className="px-6 py-4">{t.inviter}</th>
                                  <th className="px-6 py-4">{t.tokensHeld}</th>
                                  <th className="px-6 py-4">Actions</th>
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-700">
                              {filteredKols.map(kol => (
                                  <tr key={kol.id} className="hover:bg-slate-700/30 transition-colors">
                                      <td className="px-6 py-4">
                                          <div className="flex items-center gap-3">
                                              <img src={kol.avatar} alt="" className="w-8 h-8 rounded-full" />
                                              <div>
                                                  <p className="font-bold text-white text-sm">{kol.name}</p>
                                                  <p className="text-xs text-slate-400">{kol.handle}</p>
                                              </div>
                                          </div>
                                      </td>
                                      <td className="px-6 py-4">
                                          <span className={`font-bold ${kol.trustScore > 90 ? 'text-emerald-400' : 'text-amber-400'}`}>
                                              {kol.trustScore}
                                          </span>
                                      </td>
                                      <td className="px-6 py-4 text-white font-mono">
                                          ${kol.totalEarnings.toLocaleString()}
                                      </td>
                                      <td className="px-6 py-4">
                                          <div className="flex items-center gap-1">
                                              <Share2 size={14} className="text-indigo-400" />
                                              <span className="text-white">{kol.inviteCount}</span>
                                          </div>
                                      </td>
                                      <td className="px-6 py-4 text-sm text-slate-300">
                                          {kol.invitedBy}
                                      </td>
                                      <td className="px-6 py-4 text-emerald-400 font-mono text-sm">
                                          {kol.tokensHeld.toLocaleString()} LNK
                                      </td>
                                      <td className="px-6 py-4">
                                          <button className="text-xs bg-slate-700 hover:bg-indigo-600 text-white px-3 py-1.5 rounded transition-colors">
                                              View Full Data
                                          </button>
                                      </td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};
